import { useEffect, useRef } from "react";
import gsap from "gsap";

const Marquee = ({
  text = "FAQ?",
  speed = 16,
  backgroundColor = "transparent",
  textColor = "#cc5e15",
  fontSize = "12px",
  gap = 18,
  height = 47,
  width = 300,
  align = "center",
}) => {
  const trackRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Use a clean, precise width calculation for infinite seamless loop
    const totalWidth = track.scrollWidth / 2;

    const tween = gsap.to(track, {
      x: -totalWidth,
      duration: speed,
      ease: "none",
      repeat: -1,
      modifiers: {
        x: gsap.utils.unitize((x) => parseFloat(x) % totalWidth),
      },
    });

    return () => tween.kill();
  }, [speed]);

  const items = Array(12).fill(text);
  const alignmentClasses = {
    left: "ml-4 mr-auto",
    center: "mx-auto",
    right: "ml-auto mr-4"
  };

  return (
    <div
      className={`relative overflow-hidden ${alignmentClasses[align]}`}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        background: backgroundColor,
        
        // Anti-aliasing / subpixel shift fix for layouts using masks
        WebkitBackfaceVisibility: "hidden",
        backfaceVisibility: "hidden",

        // Fade the text itself while keeping the background image visible
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)",
        maskImage:
          "linear-gradient(to right, transparent 0%, black 15%, black 85%, transparent 100%)",

        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",

        WebkitMaskSize: "100% 100%",
        maskSize: "100% 100%",
      }}
    >
      <div
        ref={trackRef}
        /* FIXES APPLIED HERE:
          1. Changed 'flex' to 'inline-flex' to lock down explicit baseline calculation bounds.
          2. Explicitly forced height to 100% to match container constraints perfectly.
          3. Replaced 'will-change-transform' with standard CSS perspective/transform hooks 
             which natively forces the GPU rendering stack to stay perfectly locked on scroll.
        */
        className="inline-flex items-center h-full whitespace-nowrap"
        style={{
          gap: `${gap}px`,
          width: "max-content",
          transform: "translate3d(0,0,0)",
          WebkitTransform: "translate3d(0,0,0)"
        }}
      >
        {[...items, ...items].map((item, index) => (
          <span
            key={index}
            /* 4. Added 'inline-block' and 'align-middle' to text wrappers to strictly prevent
                 subpixel baseline shifting caused by modern screen refresh rate syncing.
            */
            className="shrink-0 font-bold uppercase inline-block align-middle select-none"
            style={{
              color: textColor,
              fontSize,
              fontFamily: "Manrope, sans-serif",
              lineHeight: `${height}px`, // Locks text line-height completely to container height
            }}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

export default Marquee;